import { Realm, PartialPick, REALM } from "../../../../constants";
import { AnnouncementType, ClientSideConfig, ConfigFeatureToggles, RootState } from "../../../../models";
import { Announcement, Flash, GeneralState } from "../../../../models/state";
import {
    Appearance,
    AppearancePreference,
    parseAppearance,
    parseAppearancePreference,
    resolveAppearance,
} from "../../../../shared/appearance";

export type State = PartialPick<RootState, "general" | "config">;

export const getConfig: (state: State) => ClientSideConfig | undefined = (state) => state?.config;
export const getEnvironment: (state: State) => string | undefined = (state) => getConfig(state)?.environment;
export const getAppearancePreference: (state: State) => AppearancePreference = (state) =>
    parseAppearancePreference(getConfig(state)?.appearance);
export const getSystemAppearance: (state: State) => Appearance = (state) =>
    parseAppearance(getConfig(state)?.systemAppearance);
export const getSupportPhoneNumber: (state: State) => string = (state) =>
    getConfig(state)?.supportPhoneNumber || "92405050";
export const getHwOnlyMaximumUpfronLimit: (state: State) => number = (state) =>
    getConfig(state)?.hwOnlyMaximumUpfronLimit || 1000;
// Features
export const getFeatureToggles: (state: State) => Partial<ConfigFeatureToggles> = (state) =>
    getConfig(state)?.featureToggles || {};

export const getGeneral: (state: State) => GeneralState | undefined = (state) => state?.general;
export const getContentfulFeatureToggles: (state: State) => Record<string, boolean> = (state) =>
    getGeneral(state)?.contentfulFeatureToggles || {};
export const getRealm: (state: State) => Realm = (state) => getGeneral(state)?.realm || REALM.B2C;
export const getFlash: (state: State) => Flash | undefined = (state) => getGeneral(state)?.flash;
export const getFlashMessage: (state: State) => string = (state) => getFlash(state)?.message || "";
export const isPhoneNumberAdmin: (state: State) => boolean = (state) =>
    getFlashMessage(state).startsWith("Angitt mobilnummer har ikke lov") || false;
export const getOrderId: (state: State) => string | undefined = (state) => getGeneral(state)?.orderId;
export const getOrderItemGuid: (state: State) => string = (state) => getGeneral(state)?.orderItemGuid || "";
export const getOrderRef: (state: State) => string | undefined = (state) => getGeneral(state)?.orderRef;
export const isOrderExpired: (state: State) => boolean = (state) => getGeneral(state)?.isOrderExpired === true;
export const isOrderNotFound: (state: State) => boolean = (state) => getGeneral(state)?.isOrderNotFound === true;
export const isOrderValid: (state: State) => boolean = (state) => getGeneral(state)?.isOrderValid === true;
export const hasSubmittedOrder: (state: State) => boolean = (state) => getGeneral(state)?.hasSubmittedOrder === true;
export const isOrgTheOrderer: (state: State) => boolean = (state) => getGeneral(state)?.isOrgTheOrderer === true;
export const showEmptiedOrderModal: (state: State) => boolean = (state) => !!getGeneral(state)?.showEmptiedOrderModal;
export const showLockedOrderModal: (state: State) => boolean = (state) => !!getGeneral(state)?.showLockedOrderModal;
export const showInvalidOrderModal: (state: State) => boolean = (state) => !!getGeneral(state)?.showInvalidOrderModal;
export const showNoneOrderModal: (state: State) => boolean = (state) => !!getGeneral(state)?.showNoneOrderModal;
export const getBackgroundColor: (state: State) => string = (state) => getGeneral(state)?.backgroundColor || "inherit";
export const isCheckoutCompleted: (state: State) => boolean = (state) =>
    getGeneral(state)?.checkoutCompletedNow || false;
export const isFwaCompleted: (state: State) => boolean = (state) => !!getGeneral(state)?.fwaCompletedNow;
export const isCheckout: (state: State) => boolean = (state) => getGeneral(state)?.isCheckout || false;
export const isRetailer: (state: State) => boolean = (state) => getGeneral(state)?.isRetailer || false;
/** The appearance actually rendered: user preference or OS setting, and always light off retailer pages. */
export const getAppearance: (state: State) => Appearance = (state) =>
    resolveAppearance({
        isRetailer: isRetailer(state),
        appearance: getAppearancePreference(state),
        systemAppearance: getSystemAppearance(state),
    });
export const getAgentChainName: (state: State) => string | undefined = (state) => getGeneral(state)?.agentChainName;
export const isSummary: (state: State) => boolean = (state) => getGeneral(state)?.isSummary || false;
export const isPersonalDetails: (state: State) => boolean = (state) => getGeneral(state)?.isPersonalDetails || false;
export const isMinSideSimdealOrder: (state: State) => boolean = (state) =>
    getGeneral(state)?.isMinSideSimdealOrder || false;
export const isTransfer: (state: State) => boolean = (state) => getGeneral(state)?.isTransfer || false;
export const isEsim: (state: State) => boolean = (state) => getGeneral(state)?.isEsim || false;
export const isGATransactionTracked: (state: State) => boolean = (state) =>
    getGeneral(state)?.isGATransactionTracked || false;
export const getAnnouncements: (state: State) => Announcement[] = (state) => getGeneral(state)?.announcements || [];
export const getAnnouncementInfo = (state: State, componentName?: AnnouncementType): Announcement | undefined => {
    const announcements = getAnnouncements(state);
    if (!componentName) return announcements[0]; // return only the first one, several seems to be an editor issue
    return announcements.find((announcement) => (announcement.id || []).includes(componentName));
};
export const isB2B: (state: State) => boolean = (state) => getGeneral(state)?.realm === REALM.B2B || false;
export const getErrorMessage: (state: State) => string = (state) => getGeneral(state)?.error?.message || "";
export const isAllowedToOrder: (state: State) => boolean = (state) => !!getGeneral(state)?.isAllowedToOrder;
export const isPortin: (state: State) => boolean = (state) => getGeneral(state)?.isPortin || false;
